import styled from "styled-components";
import { DesignTokens } from "melco-ui";

const { spacings, colors, borders } = DesignTokens;

// Exact height of ContentHeader's own padding-top + padding-bottom +
// border-bottom, computed from the *same* DesignTokens values
// ContentHeader.tsx itself uses — not a guess, this will always match
// whatever ContentHeader actually renders for those three properties.
const CONTENT_HEADER_CHROME_HEIGHT = spacings.xlarge * 2 + 10 + spacings.middle + borders.width.normal;

// WHAT: the three-column layout row that holds the user/machine/logs panels.
// WHY: `overflow: hidden` on the row (rather than the page) is what lets each
// child Panel scroll independently instead of the whole workspace scrolling.
//
// WHY an explicit `calc()` height instead of `flex: 1`: this row renders
// inside melco-ui's own <MainContentArea>/<ContentWrapper> (see
// screens/summit/Summit.tsx) — files we can't edit — and neither of those
// two wrappers is `display: flex`, so a plain `flex: 1` here has no bounded
// flex container to size itself against; it just grows to fit whatever's
// inside it, which pushes the whole page taller than the viewport and
// produces a page-level scrollbar instead of each column scrolling on its
// own.
//
// The two pieces subtracted below account for the ContentHeader above this
// row and the app Footer below it:
//  - `74px + 16px + 1px` is ContentHeader's own padding-top / padding-bottom
//    / border-bottom, taken directly from the *same* DesignTokens values
//    ContentHeader.tsx itself uses (spacings.xlarge*2+10, spacings.middle,
//    borders.width.normal) — this part is exact, not a guess.
//  - `--summit-header-title-height` / `--summit-footer-height` cover the
//    parts we *can't* derive from melco-ui's tokens because they come from
//    antd's own internal PageHeader/Footer markup (title line-height,
//    antd's default footer padding) rather than anything melco-ui exposes.
//    The defaults below are close estimates; if the columns end up a few
//    pixels short of the footer (or slightly overlapping it) on your
//    running app, that's the number to nudge — open dev tools, note the
//    real rendered height of the header's title row / the footer, and set
//    these two custom properties (e.g. on <body> or higher up the tree)
//    to match exactly. No other part of this calc should ever need to
//    change.
export const WorkspaceRow = styled.div`
  display: flex;
  height: calc(
    100vh - (${CONTENT_HEADER_CHROME_HEIGHT}px + var(--summit-header-title-height, 40px)) -
      var(--summit-footer-height, 70px)
  );
  overflow: hidden;
`;

// WHAT: one column of the three-column workspace.
// WHY a `width` prop instead of always using flex: the first two columns
// (user search, machines) are fixed-width by design so the logs panel gets
// whatever space is left. Passing no width falls back to `flex: 1` for that
// last, growable column.
export const Panel = styled.div<{ width?: number }>`
  ${({ width }) => (width ? `width: ${width}px; flex-shrink: 0;` : "flex: 1; min-width: 0;")}
  border-right: 1px solid ${colors.gray.light};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
`;

// WHAT: the scrollable machine-list region inside the "Select Machine"
// column.
// WHY `flex: 1; min-height: 0;` instead of a fixed `max-height: Xvh`:
// a vh unit is a fraction of the *whole viewport*, not of the space this
// panel actually has left after its header/header-row/footer are laid
// out — so on a short viewport the two happen to roughly line up, but on
// a tall one the vh cap stops way short of the real available height,
// leaving a visible gap before the footer. `flex: 1` instead tells this
// element to simply take up whatever room its flex-column parent (Panel,
// see above) has left after its own siblings, so it always ends exactly
// where the panel ends — the footer never gets orphaned below empty
// space. `min-height: 0` is required alongside `flex: 1` on a flex child
// that also scrolls: without it, a flex item's default `min-height:auto`
// lets it grow to fit its content instead of shrinking to the space
// available, which would silently defeat `overflow-y: auto` (the box
// would just grow instead of scrolling).
export const ScrollableDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  `

// WHAT: the scrollable table region inside the "Logs" column, sitting
// above the (always-visible) pagination footer.
// WHY the same `flex: 1; min-height: 0` fix as ScrollableDiv above: same
// root cause (a vh-based max-height doesn't track the panel's real
// available height), same fix. Here it also matters that
// PaginationFooter is a sibling *after* this element in the same flex
// column (see LogsTable.tsx) — flex: 1 makes the table claim only the
// leftover space above that footer, so the footer itself never gets
// pushed off-screen or floats in empty space.
export const LogsTableDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  `

// "STEP N — ..." eyebrow label used above each panel's content.
export const StepLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.gray.middle};
  margin-bottom: 9px;
`;

export const PanelHeading = styled.div`
  padding: 0px ${spacings.middle}px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.gray.middle};
  border-bottom: 1px solid ${colors.gray.light};
  flex-shrink: 0;
`;

// Generic "nothing to show yet" placeholder, reused for every empty/prompt
// state across all three panels (select a user, select an add-on, select a
// machine, no machines found, etc.) so those states look consistent.
export const CenteredMessage = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacings.large}px ${spacings.middle}px;
  text-align: center;
  font-size: 13px;
  color: ${colors.gray.middle};
`;

export const Breadcrumb = styled.div`
  padding: 6px ${spacings.middle}px;
  border-bottom: 1px solid ${colors.gray.light};
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  background: ${colors.gray.superLight};
  font-size: 12px;
`;

export const Crumb = styled.span<{ active?: boolean }>`
  padding: 2px 9px;
  border-radius: 10px;
  color: ${({ active }) => (active ? colors.main.negative : colors.gray.dark)};
  font-weight: ${({ active }) => (active ? 600 : 400)};
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacings.small}px;
  flex-wrap: wrap;
  padding: 9px ${spacings.middle}px;
  border-bottom: 1px solid ${colors.gray.light};
  background: ${colors.gray.superLight};
  flex-shrink: 0;
`;

export const MachineLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.gray.dark};
`;

export const ShowAllMachinesRow = styled.div`
  padding: ${spacings.small}px ${spacings.middle}px;
  border-bottom: 1px solid ${colors.gray.light};
  font-size: 12px;
  color: ${colors.gray.dark};
  flex-shrink: 0;
`;
